import { useCallback, useEffect, useEffectEvent, useRef, useState } from 'react';
import NavigateBefore from '@mui/icons-material/NavigateBefore';
import NavigateNext from '@mui/icons-material/NavigateNext';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {
  getFormPages,
  getPageImage,
  type FormDetail,
  type PageItem,
  type PageManifest,
} from '../api/catalog';
import { ApiError } from '../api/httpClientFactory';
import { COPY } from '../copy';
import { PreviewUnavailable } from './PreviewUnavailable';
import { recordViewOnce } from './recordView';

interface PageManifestState {
  status: 'loading' | 'ready' | 'unavailable' | 'error';
  manifest: PageManifest | null;
  error: ApiError | null;
  retry: () => void;
  /** Re-reads the page list; true (and the new manifest adopted) only when `versionNo` changed. */
  recheck: () => Promise<boolean>;
}

function asApiError(error: unknown): ApiError {
  return error instanceof ApiError ? error : new ApiError('UNKNOWN', 0);
}

/** The page list, with `useFormDetail`'s generation-and-abort pattern. */
function usePageManifest(formId: number): PageManifestState {
  const [status, setStatus] = useState<PageManifestState['status']>('loading');
  const [manifest, setManifest] = useState<PageManifest | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [retryToken, setRetryToken] = useState(0);
  const [seenId, setSeenId] = useState(formId);
  const [seenRetry, setSeenRetry] = useState(retryToken);

  if (seenId !== formId || seenRetry !== retryToken) {
    setSeenId(formId);
    setSeenRetry(retryToken);
    setError(null);
    setStatus('loading');
    setManifest(null);
  }

  const generation = useRef(0);
  const controllerRef = useRef<AbortController | null>(null);
  const versionRef = useRef<number | null>(null);
  const retry = useCallback(() => {
    setRetryToken((token) => token + 1);
  }, []);

  useEffect(() => {
    const gen = generation.current + 1;
    generation.current = gen;
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    versionRef.current = null;

    getFormPages(formId, controller.signal).then(
      (next) => {
        if (generation.current !== gen) return;
        versionRef.current = next.versionNo;
        setManifest(next);
        setStatus('ready');
      },
      (reason: unknown) => {
        if (generation.current !== gen) return;
        if (reason instanceof ApiError && reason.code === 'SESSION_EXPIRED') return;
        const apiError = asApiError(reason);
        if (apiError.status === 404) {
          setStatus('unavailable');
          return;
        }
        setError(apiError);
        setStatus('error');
      },
    );

    return () => controller.abort();
  }, [formId, retryToken]);

  useEffect(() => {
    return () => {
      generation.current += 1;
      controllerRef.current?.abort();
    };
  }, []);

  const recheck = useCallback(async (): Promise<boolean> => {
    const gen = generation.current;
    try {
      const next = await getFormPages(formId);
      if (generation.current !== gen || next.versionNo === versionRef.current) return false;
      versionRef.current = next.versionNo;
      setManifest(next);
      return true;
    } catch {
      return false;
    }
  }, [formId]);

  return { status, manifest, error, retry, recheck };
}

function pageNoOf(target: Element): number | null {
  const value = Number((target as HTMLElement).dataset.pageNo);
  return Number.isInteger(value) && value >= 1 ? value : null;
}

interface PageViewerProps {
  /** Kind XDP, with `pageCount` a number. */
  form: FormDetail;
  /** From the URL; null is page 1. */
  page: number | null;
  /** The parent writes `?page` with replace (V5). */
  onPage: (page: number) => void;
}

/** The XDP Form tab's page images: lazy (V3), current page (V4), `?page` (V5), 404 rule. */
export function PageViewer({ form, page, onPage }: PageViewerProps) {
  const { status, manifest, error, retry, recheck } = usePageManifest(form.id);
  const frames = useRef(new Map<number, HTMLElement>());
  const ratios = useRef(new Map<number, number>());
  const currentRef = useRef<number | null>(null);
  const rechecked = useRef(false);
  const pendingRecheck = useRef<Promise<boolean> | null>(null);
  const [near, setNear] = useState<ReadonlySet<number>>(() => new Set());
  const [reported, setReported] = useState<number | null>(null);

  const pageCount = manifest?.pageCount ?? 0;
  const versionNo = manifest?.versionNo;
  const clamp = (n: number | null) => (n !== null && n >= 1 && n <= pageCount ? n : 1);
  const current = clamp(reported ?? page);

  const scrollToPage = (n: number) => {
    frames.current.get(n)?.scrollIntoView({ block: 'start' });
  };

  useEffect(() => {
    if (status === 'ready') recordViewOnce(form.id, form.versionNo);
  }, [status, form.id, form.versionNo]);

  useEffect(() => {
    rechecked.current = false;
    pendingRecheck.current = null;
  }, [versionNo]);

  const onMissing = useCallback((): Promise<boolean> => {
    if (pendingRecheck.current) return pendingRecheck.current;
    if (rechecked.current) return Promise.resolve(false);
    rechecked.current = true;
    const pending = recheck();
    pendingRecheck.current = pending;
    return pending;
  }, [recheck]);

  const reportPage = useEffectEvent((n: number) => {
    onPage(n);
  });

  useEffect(() => {
    if (!manifest) return;
    ratios.current.clear();
    const nearObserver = new IntersectionObserver(
      (entries) => {
        setNear((previous) => {
          let next: Set<number> | null = null;
          for (const entry of entries) {
            const n = pageNoOf(entry.target);
            if (n === null) continue;
            const has = (next ?? previous).has(n);
            if (entry.isIntersecting && !has) {
              next ??= new Set(previous);
              next.add(n);
            } else if (!entry.isIntersecting && has) {
              next ??= new Set(previous);
              next.delete(n);
            }
          }
          return next ?? previous;
        });
      },
      { rootMargin: '200% 0px' },
    );
    const ratioObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const n = pageNoOf(entry.target);
          if (n !== null) ratios.current.set(n, entry.intersectionRatio);
        }
        let best: number | null = null;
        let bestRatio = 0;
        for (const [n, ratio] of ratios.current) {
          if (
            ratio > bestRatio ||
            (ratio > 0 && ratio === bestRatio && best !== null && n < best)
          ) {
            best = n;
            bestRatio = ratio;
          }
        }
        if (best === null || best === currentRef.current) return;
        currentRef.current = best;
        setReported(best);
        reportPage(best);
      },
      { threshold: [0, 0.25, 0.5, 0.75, 1] },
    );
    for (const element of frames.current.values()) {
      nearObserver.observe(element);
      ratioObserver.observe(element);
    }
    return () => {
      nearObserver.disconnect();
      ratioObserver.disconnect();
    };
  }, [manifest]);

  // V5: once per adopted version, bring the URL's page into view. An out-of-range page clears
  // `?page`. With no page in the URL the tab stays where it opened, header and downloads in view.
  const scrollOnLoad = useEffectEvent(() => {
    if (!manifest) return;
    if (page !== null && page > manifest.pageCount) onPage(1);
    const target = clamp(page);
    if (target > 1) scrollToPage(target);
  });

  useEffect(() => {
    if (versionNo !== undefined) scrollOnLoad();
  }, [versionNo]);

  if (status === 'unavailable') return <PreviewUnavailable />;

  if (status === 'error' && error) {
    return (
      <Alert
        severity="error"
        action={
          <Button onClick={retry} color="inherit">
            {COPY.tryAgain}
          </Button>
        }
      >
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          {COPY.pagesErrorTitle}
        </Typography>
        <Typography variant="body2">{COPY.reference(error.requestId ?? error.code)}</Typography>
      </Alert>
    );
  }

  if (status !== 'ready' || !manifest) {
    return <LinearProgress aria-label={COPY.loadingPages} />;
  }

  return (
    <Box>
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
          py: 1,
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <IconButton
          aria-label={COPY.previousPage}
          disabled={current <= 1}
          onClick={() => scrollToPage(current - 1)}
        >
          <NavigateBefore />
        </IconButton>
        <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {COPY.pageOf(current, manifest.pageCount)}
        </Typography>
        <IconButton
          aria-label={COPY.nextPage}
          disabled={current >= manifest.pageCount}
          onClick={() => scrollToPage(current + 1)}
        >
          <NavigateNext />
        </IconButton>
      </Box>
      <Stack component="section" spacing={2} aria-label={COPY.formPagesLabel}>
        {manifest.pages.map((item) => (
          <Box
            key={`${manifest.versionNo}:${item.pageNo}`}
            data-page-no={item.pageNo}
            ref={(element: HTMLElement | null) => {
              if (!element) return;
              frames.current.set(item.pageNo, element);
              return () => {
                if (frames.current.get(item.pageNo) === element) frames.current.delete(item.pageNo);
              };
            }}
            style={{ aspectRatio: `${item.width} / ${item.height}` }}
            sx={{
              width: '100%',
              border: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
              // The sticky page bar must not cover the top of a page scrolled to by Previous/Next.
              scrollMarginTop: (theme) => theme.spacing(8),
            }}
          >
            <PageFrame
              formId={form.id}
              versionNo={manifest.versionNo}
              item={item}
              total={manifest.pageCount}
              title={form.title}
              near={near.has(item.pageNo)}
              onMissing={onMissing}
            />
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

interface PageFrameProps {
  formId: number;
  versionNo: number;
  item: PageItem;
  total: number;
  title: string;
  near: boolean;
  /** The 404 rule: resolves true when the viewer adopted a new version. */
  onMissing: () => Promise<boolean>;
}

type FrameState = { kind: 'idle' } | { kind: 'ready'; url: string } | { kind: 'error' };

const IDLE: FrameState = { kind: 'idle' };

function PageFrame({ formId, versionNo, item, total, title, near, onMissing }: PageFrameProps) {
  const [state, setState] = useState<FrameState>(IDLE);
  const [attempt, setAttempt] = useState(0);
  const pageNo = item.pageNo;

  useEffect(() => {
    if (!near) return;
    let active = true;
    let url: string | null = null;
    const controller = new AbortController();

    getPageImage({ id: formId, versionNo, pageNo, signal: controller.signal }).then(
      (blob) => {
        if (!active) return;
        url = URL.createObjectURL(blob);
        setState({ kind: 'ready', url });
      },
      async (reason: unknown) => {
        if (!active) return;
        if (reason instanceof ApiError && reason.status === 404) {
          const reloaded = await onMissing();
          if (active && !reloaded) setState({ kind: 'error' });
          return;
        }
        if (reason instanceof ApiError && reason.code === 'SESSION_EXPIRED') return;
        setState({ kind: 'error' });
      },
    );

    return () => {
      active = false;
      controller.abort();
      if (url !== null) URL.revokeObjectURL(url);
      setState(IDLE);
    };
  }, [near, attempt, formId, versionNo, pageNo, onMissing]);

  // A page that is no longer near shows as idle at once; its effect cleanup revokes the URL.
  const shown = near ? state : IDLE;

  if (shown.kind === 'ready') {
    return (
      <Box
        component="img"
        src={shown.url}
        alt={COPY.pageAlt(title, pageNo, total)}
        sx={{ display: 'block', width: '100%', height: '100%' }}
      />
    );
  }

  if (shown.kind === 'error') {
    return (
      <Stack spacing={1} sx={{ height: '100%', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="body2">{COPY.pageErrorTitle}</Typography>
        <Button size="small" onClick={() => setAttempt((n) => n + 1)}>
          {COPY.tryAgain}
        </Button>
      </Stack>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Typography variant="body2" color="text.secondary">
        {COPY.loadingPage(pageNo)}
      </Typography>
    </Box>
  );
}
